import type { ChannelInfoDto, MarketingYearSeriesDto, MonthlyMarketingPerformanceDto } from '../../../api/hooks/useMarketingPerformance'
import type { Rgb } from '../../charts/comparisonColors'

export type PerformanceMetric =
  | 'totalCost'
  | 'revenueWithoutVat'
  | 'orders'
  | 'pno'
  | 'roas'
  | 'avgOrderValue'
  | 'costPerOrder'

export type MetricUnit = 'czk' | 'percent' | 'count'

export const PERFORMANCE_METRICS: PerformanceMetric[] = [
  'totalCost', 'revenueWithoutVat', 'orders', 'pno', 'roas', 'avgOrderValue', 'costPerOrder',
]

export const METRIC_LABELS: Record<PerformanceMetric, string> = {
  totalCost: 'Náklady na reklamu',
  revenueWithoutVat: 'Tržby (bez DPH)',
  orders: 'Objednávky',
  pno: 'PNO',
  roas: 'ROAS',
  avgOrderValue: 'Průměrná objednávka',
  costPerOrder: 'Cena za nákup',
}

/** Plain-Czech explanation of each metric for readers who do not work in marketing. */
export const METRIC_DESCRIPTIONS: Record<PerformanceMetric, string> = {
  totalCost: 'Kolik stála reklama — součet faktur od FB/IG, Google a S-Kliku za daný měsíc, bez DPH.',
  revenueWithoutVat: 'Kolik e-shop v daném měsíci prodal, bez DPH. Nejde o zisk, ale o obrat.',
  orders: 'Počet objednávek z e-shopu za daný měsíc.',
  pno: 'Podíl nákladů na obratu: kolik procent tržeb bez DPH padne na reklamu. Nižší je lepší.',
  roas: 'Návratnost reklamy: kolik korun tržeb bez DPH přinesla každá koruna vložená do reklamy, v procentech. 400 % znamená čtyřnásobek. Vyšší je lepší.',
  avgOrderValue: 'Průměrná hodnota jedné objednávky — tržby bez DPH dělené počtem objednávek.',
  costPerOrder: 'Kolik stálo získání jedné objednávky — náklady na reklamu dělené počtem objednávek. Nižší je lepší.',
}

/** Same, for the two chart layouts in the "Zobrazení" selector. */
export const VIEW_MODE_DESCRIPTIONS: Record<'trend' | 'comparison', string> = {
  trend: 'Vývoj v čase: měsíce jdou za sebou na jedné ose, takže je vidět dlouhodobý trend a sezónnost.',
  comparison: 'Meziroční srovnání: leden až prosinec vedle sebe pro několik let najednou, takže je vidět, jak si stejný měsíc vedl oproti loňsku.',
}

export const METRIC_UNITS: Record<PerformanceMetric, MetricUnit> = {
  totalCost: 'czk',
  revenueWithoutVat: 'czk',
  orders: 'count',
  pno: 'percent',
  roas: 'percent',
  avgOrderValue: 'czk',
  costPerOrder: 'czk',
}

export const METRIC_COLORS: Record<PerformanceMetric, Rgb> = {
  totalCost: [239, 68, 68],        // red-500
  revenueWithoutVat: [34, 197, 94], // green-500
  orders: [59, 130, 246],          // blue-500
  pno: [249, 115, 22],             // orange-500
  roas: [168, 85, 247],            // purple-500
  avgOrderValue: [20, 184, 166],   // teal-500
  costPerOrder: [245, 158, 11],    // amber-500
}

/** Stacked channel-cost bars; cycled by channel index (config order). */
export const CHANNEL_COLORS: Rgb[] = [
  [59, 130, 246],  // blue — FB/IG
  [234, 179, 8],   // yellow — Google
  [220, 38, 38],   // red — S-Klik
  [107, 114, 128], // gray — any further channel
]

/**
 * Channel codes identify the same logical channel regardless of case.
 *
 * The server groups stored costs with `StringComparer.OrdinalIgnoreCase` because the Postgres
 * unique index on (MonthId, ChannelCode) is case-sensitive and lets "meta"/"META" coexist. It
 * normalises configured codes to their config casing, but an orphan keeps whatever casing it was
 * first stored with *in that month* — so the same orphan can arrive as "Sklik" in one month and
 * "sklik" in the next. Matching case-sensitively here would split it into two legend entries.
 */
const normalizeChannelCode = (code: string): string => code.toLowerCase()

/** Cost booked to one channel in one month; 0 when the channel has no row that month. */
export const findChannelCost = (row: MonthlyMarketingPerformanceDto, code: string): number =>
  row.channelCosts?.find((c) => normalizeChannelCode(c.channelCode) === normalizeChannelCode(code))
    ?.costWithoutVat ?? 0

/**
 * The channel list to draw a cost breakdown from.
 *
 * `channels` carries only the configured channels, but `totalCost` is the sum of every stored
 * channel cost — the server deliberately appends rows for codes that are no longer in config so
 * nothing disappears silently. Drawing the configured list alone would make the stacked columns
 * add up to less than the "Náklady" card and the "Náklady celkem" table column on the same page.
 * Configured channels keep their config order (and therefore their colour); orphans follow.
 */
export const resolveBreakdownChannels = (
  channels: ChannelInfoDto[],
  rows: MonthlyMarketingPerformanceDto[],
): ChannelInfoDto[] => {
  const seen = new Set(channels.map((c) => normalizeChannelCode(c.code)))
  const orphans: ChannelInfoDto[] = []

  for (const row of rows) {
    for (const cost of row.channelCosts ?? []) {
      const key = normalizeChannelCode(cost.channelCode)
      if (seen.has(key)) continue
      seen.add(key)
      // The DTO types `label` as required, but it comes off the wire — fall back to the code
      // rather than rendering an empty legend entry if the server ever omits it.
      orphans.push({ code: cost.channelCode, label: cost.label || cost.channelCode } as ChannelInfoDto)
    }
  }

  return orphans.length === 0 ? channels : [...channels, ...orphans]
}

/**
 * Drops the month still in progress.
 *
 * A running month has a full month of revenue missing but can already carry a credit note, so its
 * ratio metrics land orders of magnitude off (a single -7 350 Kč dobropis produced ROAS -14 610 %)
 * and flatten every other month in the chart against the axis. Keyed off `isPartial` rather than the
 * month number, so in the year-over-year view only the anchor year's month is dropped — the same
 * month in earlier years is finished data and stays.
 *
 * Returns the input untouched when nothing is partial, so useMemo consumers keep their reference.
 */
export const withoutPartialMonths = (
  rows: MonthlyMarketingPerformanceDto[],
): MonthlyMarketingPerformanceDto[] =>
  rows.some((m) => m.isPartial) ? rows.filter((m) => !m.isPartial) : rows

/** withoutPartialMonths, applied per year. Returns new series objects; never mutates the input. */
export const withoutPartialMonthsInSeries = (
  series: MarketingYearSeriesDto[],
): MarketingYearSeriesDto[] =>
  series.some((s) => s.months.some((m) => m.isPartial))
    ? series.map((s) => ({ ...s, months: withoutPartialMonths(s.months) }) as MarketingYearSeriesDto)
    : series

export const MONTH_LABELS_SHORT = [
  'Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro',
] as const

export const getMetricValue = (row: MonthlyMarketingPerformanceDto, metric: PerformanceMetric): number | null => {
  const value = row[metric]
  return value === null || value === undefined ? null : Number(value)
}

const czk = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const count = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 })
const percent = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

export const EMPTY_VALUE = '—'

export const formatCzk = (value: number): string => czk.format(value)
export const formatCount = (value: number): string => count.format(value)
export const formatPercent = (value: number): string => `${percent.format(value)} %`

export const formatMetric = (value: number | null | undefined, unit: MetricUnit): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  switch (unit) {
    case 'czk':
      return formatCzk(value)
    case 'percent':
      return formatPercent(value)
    case 'count':
      return formatCount(value)
    default: {
      const _exhaustive: never = unit
      throw new Error(`Unhandled unit: ${_exhaustive}`)
    }
  }
}

/** YoY ratio arrives as "this year / last year × 100"; show it as a signed delta ("+12,3 %"). */
export const formatYoy = (ratioPercent: number | null | undefined): string => {
  if (ratioPercent === null || ratioPercent === undefined || Number.isNaN(ratioPercent)) return EMPTY_VALUE
  const delta = ratioPercent - 100
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : ''
  return `${sign}${percent.format(Math.abs(delta))} %`
}
